import * as React from 'react';

import QuestionsAndAnswers from './QuestionsAndAnswers'

import NovelBasedActivityPacks from '../components/content_hubs/novel_based_activity_packs'
import SubjectPageTeacherCenterArticles from '../components/content_hubs/subject_page_teacher_center_articles'
import SubjectPageSummary from '../components/content_hubs/subject_page_summary'
import { GETTING_STARTED_COLLECTION_ID, INTEGRATING_QUILL_EVIDENCE_ARTICLE_ID } from '../utils/subjectPages';
import { FISHTANK_ELA_GRADE_7_SLUG, FISHTANK_ELA_GRADE_8_SLUG } from '../../Shared/index'

const pageImgBaseSrc = `${window.postbuildEnv.CDN_URL}/images/pages/literature_subject_page`

const fishtankLogoSrc = `${pageImgBaseSrc}/fishtank-logo.png`
const andIconSrc = `${pageImgBaseSrc}/and-icon.svg`
const quillLogoGreenSrc = `${window.postbuildEnv.CDN_URL}/images/logos/quill-logo-green.svg`

const summary = (
  <SubjectPageSummary
    paragraphCopy="Written and reviewed by educators, each Quill Literature text supports the instruction of a work of literature. Not only do these activities provide key historical context and background knowledge, they also provide support for:"
  />
)

const teacherQuotes = (
  <section className="teacher-quotes">
    <div className="container">
      <h2>What Are English Language Arts Teachers Saying?</h2>
      <div className="quote-boxes">
        <div className="quote-box">
          <p>"Because the tasks are connected to the novel students are already reading, the work feels purposeful and relevant rather than disconnected skill practice. They also reinforce key literacy skills—close reading, citing evidence, constructing clear responses, and academic language use—while giving students repeated practice in a consistent format.”</p>
          <div>
            <span className="name">Christina H.</span>
            <span>Liverpool Central School District</span>
            <span>Literacy Instructional Coach</span>
          </div>
        </div>
        <div className="quote-box">
          <p>“The three sentence stems (because, but, so) require true comprehension of the central idea of the article. In other assignments, kids might answer &apos;What&apos;s the central idea of the article?&apos; inaccurately, landing on a main subject or topic of the article. But in these Reading for Evidence activities, they&apos;re required to understand what&apos;s being said about that topic, from three different lenses. That&apos;s a great way to push them toward the right understanding.”</p>
          <div>
            <span className="name">Kristen S.</span>
            <span>North Warren Central School</span>
            <span>7th and 8th grade English Language Arts</span>
          </div>
        </div>
      </div>
    </div>
  </section>
)

const partners = (
  <section className="partners container">
    <div className="partner-logos">
      <img alt="Quill logo" src={quillLogoGreenSrc} />
      <img alt="and" className="and-icon" src={andIconSrc} />
      <img alt="Fishtank Learning logo" className="fishtank-logo" src={fishtankLogoSrc} />
    </div>
    <p>Quill has partnered with <a className="focus-on-light" href="https://www.fishtanklearning.org/" rel="noopener noreferrer" target="_blank">Fishtank Learning</a> to design Quill Literature activities that align with Fishtank ELA. You can use the Quill activities as part of the Fishtank ELA units to build core knowledge that enhances students&apos; experiences of the unit texts while focusing on developing their writing. Whether used on their own or in conjunction with Fishtank, these resources are a valuable addition to any ELA&nbsp;classroom.</p>
  </section>
)

function courseCard({ gradeLabel, link, active }) {
  const buttonClass = `quill-button focus-on-light contained medium teal ${active ? '' : 'disabled'}`
  const buttonLabel = active ? 'View Activities' : 'Coming Soon!'
  return (
    <div className="course-card" key={gradeLabel}>
      <img alt="" className="fishtank-logo" src={fishtankLogoSrc} />
      <div>
        <h2>Fishtank ELA</h2>
        <span>{gradeLabel}</span>
      </div>
      <a className={buttonClass} href={active ? link : undefined}>{buttonLabel}</a>
    </div>
  )
}

const LiteratureSubjectPage = ({}) => {
  const basePath = window.location.pathname
  const formatLink = (slug: string) => `${basePath}/${slug}`.replace('//', '/')
  const GRADE_7_LINK = formatLink(FISHTANK_ELA_GRADE_7_SLUG)
  const GRADE_8_LINK = formatLink(FISHTANK_ELA_GRADE_8_SLUG)

  const FISHTANK_COURSES = [
    { gradeLabel: 'Grade 6', link: null, active: false },
    { gradeLabel: 'Grade 7', link: GRADE_7_LINK, active: true },
    { gradeLabel: 'Grade 8', link: GRADE_8_LINK, active: true },
  ]

  const overview = (
    <header>
      <div className="container">
        <h1>Literature</h1>
        <p>Quill Literature deepens background knowledge, strengthens evidence-based writing, and supports the teaching of novels, poems, and other literary works. Aligned with the Science of Reading, Quill Literature pairs engaging nonfiction texts and writing activities with many of the most commonly taught books in grades 6 and up.</p>
        <p>As students complete each Quill activity, they enrich their understanding by exploring topics related to the author, historical context, and cultural significance of the literary work they&apos;re studying. They then use evidence from those texts to write claims, receive AI-powered feedback, and revise their responses to strengthen their writing.</p>
        <p className="includes-header">Quill Literature includes:</p>
        <ul className="includes-list">
          <li><strong>Curriculum-aligned collections.</strong> Activities aligned to 7th- and 8th-grade ELA curriculum from our partner Fishtank Learning, with Grade 6 coming soon. These collections include activities across Quill&apos;s most popular tools, including Reading for Evidence, as well as Quill Connect and Grammar.</li>
          <li><strong>Novel-based activity packs.</strong> Collections supporting commonly taught works—from <i>Animal Farm</i> to <i>The Giver.</i> These packs help students build the background knowledge they need through paired nonfiction texts and Reading for Evidence activities that develop evidence-based writing.</li>
        </ul>
        <div className="course-cards">
          {FISHTANK_COURSES.map(course => courseCard(course))}
        </div>
      </div>
    </header>
  )

  const tryItOutForYourself = (
    <section className="try-it-out">
      <div className="container">
        <h2>Try it out for yourself</h2>
        <div className="links-wrapper">
          <a className="quill-button focus-on-dark outlined teal large" href="/evidence/#/play?uid=500&session=a97efed7-46f0-4a86-afbe-dd2bb0d694b0" rel="noopener noreferrer" target="_blank" >Try a sample activity</a>
          <a className="quill-button focus-on-dark outlined teal large" href={GRADE_7_LINK}>View Fishtank ELA 7th Grade Activities</a>
          <a className="quill-button focus-on-dark outlined teal large" href={GRADE_8_LINK}>View Fishtank ELA 8th Grade Activities</a>
        </div>
      </div>
    </section>
  )

  return (
    <main className="literature-subject-page subject-page">
      {overview}
      <NovelBasedActivityPacks />
      {summary}
      {teacherQuotes}
      {partners}
      <QuestionsAndAnswers questionsAndAnswersFile="literature" supportLink={null} />
      <SubjectPageTeacherCenterArticles blogPostIds={[GETTING_STARTED_COLLECTION_ID, INTEGRATING_QUILL_EVIDENCE_ARTICLE_ID]} color="teal" />
      {tryItOutForYourself}
    </main>
  )
}

export default LiteratureSubjectPage
